import { useTheme } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { API_PATCH, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoStepper, { FinalPage } from '../exo/ExoStepper';
import StepRecipient from '../steps/StepRecipient';
import StepPositions from '../steps/StepPositions';
import StepCustomStartUp from '../steps/StepCustomStartUp';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import moment from 'moment';
import StepUser from '../steps/StepUser';
import { StepFiles } from '../steps/StepFiles';
import StepDelivery from '../steps/StepDelivery';
import { StepTemplate } from '../steps/StepTemplate';
import { PositionImage } from '../exo/positions/collapse-modules/PositionImage';
import { PositionDescription } from '../exo/positions/collapse-modules/PositionDescription';
import { PositionNumber } from '../exo/positions/collapse-modules/PositionNumber';
import { PositionText } from '../exo/positions/collapse-modules/PositionText';
import { PositionCheckbox } from '../exo/positions/collapse-modules/PositionCheckbox';
import { PositionCopy } from '../exo/positions/collapse-modules/PositionCopy';
import {
  hasInlineAttachments,
  StepInlineAttachments,
} from '../steps/StepInlineAttachments';
import StepTemplateMetaFields from '../steps/StepTemplateMetaFields';
const NewCustomOrder = ({
  orders,
  setOrders,
  onClose,
  startData,
  onUpdate = () => {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const fields = [
    {
      label: t('Title'),
      type: 'text',
      key: 'title',
    },
    {
      label: t('Project'),
      type: 'project', //Change to "projectSearch" when TypeProjectSearch is done and we want to make the project searchable
      key: 'project',
      apiUrl: 'projects',
    },
    {
      label: t("Recipient's Addition"),
      type: 'text',
      key: 'recipientAddition',
    },
    {
      label: t('Start Date'),
      type: 'date',
      key: 'startDate',
    },
    {
      label: t('Delivery Date'),
      type: 'date',
      key: 'deliveryDate',
    },
    {
      label: t('Introduction'),
      type: 'description',
      key: 'introduction',
      multiline: true,
      textBlockType: 'order',
    },
    {
      label: t('Document note'),
      type: 'description',
      key: 'note',
      multiline: true,
      textBlockType: 'order',
    },
    {
      label: `${t('Internal Note')} (${t('optional')})`,
      type: 'description',
      key: 'internalNote',
      multiline: true,
      textBlockType: 'order',
    },
    {
      type: 'hidden',
      key: 'sourceType',
      value: 'custom',
    },
  ];

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t('Title is required')),
    deliveryDate: Yup.string().required(t('Delivery Date is required')),
  });

  const optionalFields = [
    {
      label: `${t('Discount')} (${t('optional')})`,
      type: 'checkbox',
      key: 'discount',
    },
    {
      label: `${t('Discount Rate')} (${t('optional')})`,
      type: 'number',
      key: 'discountRate',
      decimalPlaces: 2,
    },
    {
      label: `${t('Discount Days To Pay')} (${t('optional')})`,
      type: 'number',
      key: 'discountDaysToPay',
      decimalPlaces: 0,
    },
  ];

  const steps = [
    {
      label: t('Recipient'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepRecipient
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
        />
      ),
    },
    {
      label: t('Template'),
      content: ({
        dataCollection,
        updateValidation,
        updateDataCollection,
        setSteps,
        activeStep,
      }) => (
        <StepTemplate
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          templateType="order"
          setSteps={setSteps}
          activeStep={activeStep}
        />
      ),
    },
    {
      label: t('Date & Comment'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepCustomStartUp
          fields={fields}
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          validationSchema={validationSchema}
        />
      ),
    },
    {
      label: t('Additional'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepTemplateMetaFields
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          templateMetaData={dataCollection.templateMetaData}
        />
      ),
      isHiddenFunction: (data) =>
        !Boolean(data.templateMetaData?.metaFields?.length > 0),
    },
    {
      label: t('Skonto'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepCustomStartUp
          fields={optionalFields}
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
        />
      ),
    },
    {
      label: t('Dokuments'),
      valid: true,
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepFiles
          type="ORDER_ATTACHMENT"
          dataKey="attachments"
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          filter={
            dataCollection.id
              ? `orderAttachments[has]=${dataCollection.id}`
              : 'orderAttachments[doesnothave]=0&orderImages[doesnothave]=0'
          }
        />
      ),
    },
    {
      label: t('Positions'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepPositions
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          imageType={`ORDER_ATTACHMENT`}
          hideHOAI={false}
          imageApiFilter={
            startData && startData.id
              ? `orderImages[has]=${startData.id}`
              : `orderImages[doesnothave]=0`
          }
          collapseModules={[
            { component: PositionImage, key: 'image', label: 'Image' },
            {
              component: PositionDescription,
              key: 'description',
              label: 'Description',
              textBlockType: 'order',
            },
            {
              component: PositionNumber,
              key: 'taxRate',
              label: 'Tax Rate',
              unit: '%',
              value: 19,
              decimalPlaces: 0,
            },
            {
              component: PositionText,
              key: 'unit',
              label: 'Unit',
            },
            {
              component: PositionCheckbox,
              key: 'isOptional',
              label: 'Optional Position',
            },
            {
              component: PositionCopy,
              label: 'Duplicate position',
            },
          ]}
        />
      ),
    },
    {
      label: t('Attachments'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepInlineAttachments
          dataCollection={dataCollection}
          updateDataCollection={updateDataCollection}
          updateValidation={updateValidation}
        />
      ),
      isHiddenFunction: (data) => !hasInlineAttachments(data?.positions),
    },
    {
      label: t('Contact Person'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepUser
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
        />
      ),
    },
    {
      label: t('Delivery'),
      content: ({ dataCollection, updateValidation, updateDataCollection }) => (
        <StepDelivery
          updateDataCollection={updateDataCollection}
          dataCollection={dataCollection}
          updateValidation={updateValidation}
          allowMailContent={true}
          textBlockType={'order'}
        />
      ),
    },
  ];
  const config = useConfig();
  const finalStep = {
    operation: ({
      dataCollection,
      handleResponse,
      setSuccess,
      setIsLoading,
      handleError,
    }) => {
      setIsLoading(true);

      const isUpdate = Boolean(dataCollection.id);

      DBRequest({
        config,
        path: isUpdate ? `orders/${dataCollection.id}` : 'orders',
        method: isUpdate ? API_PATCH(dataCollection) : API_POST(dataCollection),
        onResponse: handleRes,
        onLoading: setIsLoading,
        onError: handleError,
      });

      function handleRes(data) {
        handleResponse(data);
        onUpdate(data);
      }
    }, // your operation with the data
    content: (response) => (
      <FinalPage response={response} data={orders} setData={setOrders} />
    ), // component with final operations and the feedback
  };

  const [defaultData, setDefaultData] = useState({});

  useEffect(() => {
    if (!config) return;
    const today = moment().format();
    const newDefaultData = {
      startDate: today,
      deliveryDate: today,
      sourceType: 'custom',
    };
    setDefaultData(newDefaultData);
  }, [config]);

  return (
    <ExoStepper
      allowDraft
      draftType={'custom-order'}
      label={startData && startData.id ? t('Edit Order') : t('New Order')}
      message={t('Create Order')}
      startDataCollection={startData || defaultData}
      onClose={onClose}
      data={steps}
      finalStep={finalStep}
      previewDocument={'order'}
    />
  );
};

export default NewCustomOrder;
